import React from 'react';
import { Status, STATUSES } from '@/types/store';
import bookList from '@/constants/booklist';
import { AssignmentGroupData, Maybe, TaskNote } from '@/graphql/types';
import { ASSIGNMENT_TYPES } from '@/constants/assignments';
import { t, TFunction } from 'i18next';
import { toast } from 'react-toastify';
import { WorkspaceTask } from '@/types/assignments';
import { clearUserFlow } from './userFlow';
import { errorMessages } from './errorMessages';
import i18n from '@i18n/i18n.config';
import { wordToNumberMap } from '@/utils/getModuleName';

// NOTE: KEY and value should be same
export const localStorageKey = {
  wovenToken: 'wovenToken',
  profile: 'profile',
  defaultLocalLanguage: 'defaultLocalLanguage',
  lastActiveRoute: 'lastActiveRoute',
  lastLoggedInEmail: 'lastLoggedInEmail',
};
// TODO: come back here to manage project level user access instead just user roles
export const rolesObject = {
  contributor: { role_type: 'Contributor', id: 1 },
  admin: { role_type: 'Admin', id: 2 },
  QALead: { role_type: 'QA Lead', id: 3 },
  superAdmin: { role_type: 'Super Admin', id: 4 },
} as const;

export type RoleType = typeof rolesObject[keyof typeof rolesObject]['role_type'];

export type Direction = 'ltr' | 'rtl' | 'auto';

// String value might be change but Id will be same
export const projectViewAccess = (role_id?: number) => {
  // block project view only for contributor role

  // TODO: Check Admin and Super Admin roles first, then return false
  // to avoid potential bugs from future role additions.
  if (!role_id || ([rolesObject.contributor.id, rolesObject.QALead.id] as number[]).includes(role_id)) {
    return false;
  }
  return true;
};
export const checkRoleAccess = (
  roles: { id: number }[],
  roleId?: number,
): boolean => {
  return roles.some((role: { id: number }) => role.id === roleId);
};

export const getLocalStorageValue = (key: string, defaultValues = '') => {
  const localValue = localStorage.getItem(key);
  if (localValue && localValue !== 'undefined') {
    try {
      return JSON.parse(localValue);
    } catch {
      return localValue;
    }
  }
  return defaultValues;
};

export const setLocalStorageValue = (key: string, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const removeLocalStorageValues = () => {
  const ignoreLocalKey = [
    localStorageKey.defaultLocalLanguage,
    localStorageKey.lastActiveRoute,
    localStorageKey.lastLoggedInEmail,
  ];
  Object.keys(localStorageKey).forEach((key: string) => {
    if (!ignoreLocalKey.includes(key)) localStorage.removeItem(key);
  });
};

// Clear logout function - removes user data and session
export const logout = () => {
  removeLocalStorageValues();
  clearUserFlow();
};

export const scrollElement = (elementId: string) => {
  const elementToScroll = document.getElementById(elementId);

  if (elementToScroll) {
    elementToScroll.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    setTimeout(() => {
      elementToScroll.focus();
    }, 500);
  }
};

export const generateRandomTaskTitle = (): string => {
  const book = bookList[Math.floor(Math.random() * bookList.length)];
  const chapter = Math.ceil(Math.random() * 10);
  const verse = Math.ceil(Math.random() * 60);
  return `${book} ${chapter}:${verse}`;
};

export const checkIsActionLoading = (currentStatus: Status) => {
  return currentStatus === STATUSES.PENDING || currentStatus === STATUSES.IDLE;
};

/**
 * List of routes that does not require authentication
 */
const unProtectedRoutes = [
  '/login',
  '/session-error',
  '/forgot-password',
  '/reset-password',
];

export const isRouteProtected = (route: string): boolean => {
  if (!route || route === '/') {
    return false;
  }

  return !unProtectedRoutes.some(authRoute =>
    route.startsWith(authRoute),
  );
};

export const getPageByAssignmentType = (assignment_type_id: number): string => {
  switch (assignment_type_id) {
    case ASSIGNMENT_TYPES.PEER_REVIEW:
    case ASSIGNMENT_TYPES.TRANSLATE_EDIT:
      return 'peer-review';
    case ASSIGNMENT_TYPES.MODEL_ASSESSMENT:
      return 'model-assessment';
    case ASSIGNMENT_TYPES.PRONOUNS:
      return 'pronouns';
    case ASSIGNMENT_TYPES.MODEL_SELECTION:
      return 'model-selection';
    case ASSIGNMENT_TYPES.REFDOCS:
      return 'ref-doc';
    case ASSIGNMENT_TYPES.TRANSLATE:
    case ASSIGNMENT_TYPES.PEER_REVIEW_VERSE:
      return '';
    default:
      return '';
  }
};

export const getAssignmentURL = (projectId: number, assignment: AssignmentGroupData): string => {
  const assignmentType = assignment.assignment_type_id;
  const pageName = getPageByAssignmentType(Number(assignmentType));
  return `/projects/${projectId}/assignment/${assignment.id}/${pageName}`;
};

export const hasAuthToken = () => getLocalStorageValue(localStorageKey.wovenToken);

export const isSessionStorageAvailable = typeof Storage !== 'undefined';

export const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export const getAssignmentName = (assignmentName: string, t: TFunction): string => {
  const parts = assignmentName.split('-');
  const type = parts[0];
  if (type === '1200-verses') {
    return t('assignment-names.1200-verses');
  }
  if (type === 'selected' && parts[1] === 'verses') {
    return t('assignment-names.selected-verses-range', { range: parts.slice(2).join('-') });
  }

  if (type === 'chapter') {
    return t('assignment-names.chapter', { chapterNum: parts[1] });
  }

  if (assignmentName.endsWith('-review')) {
    const rawBookParts = parts.slice(0, -1);

    const mappedBookNumber = wordToNumberMap[rawBookParts[0]?.toLowerCase()];
    rawBookParts[0] = mappedBookNumber ? String(mappedBookNumber) : rawBookParts[0];

    const formattedBookKey = rawBookParts.map(capitalize).join(' ');
    const translatedBookName = t(`bookList.${formattedBookKey}`, { defaultValue: formattedBookKey });

    return t('assignment-names.review-name', { reviewName: translatedBookName });
  }

  return t(`assignment-names.${assignmentName}`, { defaultValue: assignmentName });
};

// Added unknown assignment type to avoid undefined
export const getAssignmentType = (assignment_type_id: number, has_machine_translation: boolean): string => {
  if (has_machine_translation && assignment_type_id === ASSIGNMENT_TYPES.TRANSLATE) {
    return 'Edit';
  }
  const assignmentTypes: Record<number, string> = {
    1: 'Translate',
    2: 'Review',
    3: 'Assess',
    4: 'Translate',
    5: 'Assess',
    6: 'Translate',
    7: 'Review',
    8: 'Edit',
  };
  return assignmentTypes[assignment_type_id] || 'Unknown Assignment Type';
};

type ToastType = 'success' | 'error';

export const showToast = (toastId: string, type: ToastType) => {
  const messageNamespace = type === 'success' ? 'successMessages' : 'errorMessages';
  const translationKey = `${messageNamespace}.${toastId}`;
  const message = t(translationKey);

  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      render: message,
      type,
      autoClose: 6000,
    });
  } else {
    toast(message, {
      type,
      toastId,
      autoClose: 6000,
    });
  }
};

export const getTaskElementId = (task: WorkspaceTask, appendString = '') =>
  `${task.bookId}-${task.chapterId}-${task.verseId}-${task.id}${appendString ? `-${appendString}` : ''}`;

export const handleLoginErrorMessage = (error: unknown) => {
  const LOGIN_FAILED_MESSAGE = 'Login failed. Remaining attempts:';

  // Handling remaining attempts in login error messages
  if (typeof error === 'string' && error.includes(LOGIN_FAILED_MESSAGE)) {
    return error.split(LOGIN_FAILED_MESSAGE)[1]?.trim();
  }

  return `${error}`;
};

// Extracts the error message from a GraphQL error object or a generic error object
export const extractGraphQLErrorMessage = (
  error: Error,
): string => {
  let message: string = errorMessages.unknownError;

  try {
    if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      // @ts-expect-error: dynamic error shape from GraphQL
      const networkErrors = error.networkError?.result?.errors;
      // @ts-expect-error: dynamic error shape from GraphQL
      const graphQLErrors = error.graphQLErrors;

      if (Array.isArray(networkErrors) && networkErrors.length > 0) {
        message = networkErrors[0]?.message ?? errorMessages.unknownError;
      } else if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
        message = graphQLErrors[0]?.message ?? errorMessages.unknownError;
      } else if ('message' in error && typeof error.message === 'string') {
        message = error.message;
      }
    }
  } catch {
    message = errorMessages.unknownError;
  }
  return message;
};

export const getNotes = (notes?: Array<Maybe<TaskNote>>) => {
  return notes?.filter((note): note is TaskNote => note !== null && note !== undefined)
    .map(note => ({
      id: note.id,
      text: note.text,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
      userDetails: {
        ...note.userDetails,
        role: i18n.t(note.userDetails?.role ?? 'Role'),
      },
    })) || [];
};

export const createInteractiveProps = (fn: (e: React.MouseEvent | React.KeyboardEvent) => void, options?: { stopPropagation?: boolean }) => {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: (event: React.MouseEvent) => {
      if (options?.stopPropagation) event.stopPropagation();
      fn(event);
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (options?.stopPropagation) event.stopPropagation();
        if (event.repeat) return;
        fn(event);
      }
    },
  };
};

type SortDirection = 'asc' | 'desc';
type SortableValue = string | number | null | undefined;

type SortableKeys<T> = {
  [K in keyof T]-?: T[K] extends SortableValue ? K : never;
}[keyof T];

type SortConfig<T> = {
  field: SortableKeys<T>;
  order: SortDirection;
};

export function sortByObjectField<T>(
  array: T[],
  fields: SortConfig<T>[],
): T[] {
  const localeCandidate = i18n.resolvedLanguage || i18n.language;
  const locale = localeCandidate && Intl.Collator.supportedLocalesOf([localeCandidate]).length > 0
    ? localeCandidate
    : undefined;

  return [...array].sort((a, b) => {
    for (const { field, order } of fields) {
      const aValue = a[field];
      const bValue = b[field];
      const aIsEmpty = aValue === null || aValue === undefined;
      const bIsEmpty = bValue === null || bValue === undefined;

      // Keep empty values (null/undefined) at the end for both asc and desc.
      if (aIsEmpty && bIsEmpty) continue;
      if (aIsEmpty) return 1;
      if (bIsEmpty) return -1;

      const result = compareLocale(
        aValue as Exclude<SortableValue, null | undefined>,
        bValue as Exclude<SortableValue, null | undefined>,
        locale,
      );

      if (result !== 0) {
        return order === 'asc' ? result : -result;
      }
    }

    return 0;
  });
}
function compareLocale(
  aValue: string | number,
  bValue: string | number,
  locale: string | undefined,
): number {
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }

  return String(aValue).localeCompare(
    String(bValue),
    locale,
    { sensitivity: 'base', numeric: true },
  );
}

export function groupBy<T, K extends PropertyKey>(
  items: readonly T[],
  callbackFn: (element: T, index: number) => K,
): Partial<Record<K, T[]>> {
  const groups = Object.create(null) as Partial<Record<K, T[]>>;

  for (let i = 0; i < items.length; i++) {
    const element = items[i];
    const key = callbackFn(element, i);

    groups[key] ??= [];
    groups[key].push(element);
  }

  return groups;
}
